import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceDayRecord} from '@/entities/attendance/types';
import {
  formatClock,
  formatDayOfMonth,
  formatHoursFromMinutes,
} from '@/shared/lib/format';
import {Card} from '@/shared/ui/card';
import {HelpNote} from '@/shared/ui/explain';
import {FilterBar, FilterSelect} from '@/shared/ui/filter-bar';
import {Icon} from '@/shared/ui/icons';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {
  flagLabelKey,
  flagTone,
  knownFlags,
} from '@/features/view-my-attendance/lib/flags';
import {DayLog} from '@/features/view-day-log/ui/DayLog';

/**
 * One person's days, each expandable into the in→out episodes behind it.
 *
 * `DayRow` (features/view-my-attendance) shows a day as a single arrival→departure
 * pair, which is what an employee wants of their own month. A manager reading the same
 * day usually wants the next question answered too: the pair says 09:00 → 18:00 and
 * `worked_minutes` says nine hours, but was that nine hours in the building or three
 * separate visits with a two-hour gap? The `intervals` array answers it, and the two
 * durations are deliberately shown side by side:
 *
 *   `worked_minutes`   — first event to last, breaks INCLUDED
 *   `interval_minutes` — the sum of the episodes, breaks EXCLUDED
 *
 * Whichever one a payroll dispute turns on, the report has already stated both, and
 * labelled which is which — the whole reason not to show one number and call it "hours".
 *
 * Under the episodes sits the day's RAW journal (`DayLog`), when the card is told whose
 * day it is. The episodes are the engine's CONCLUSION; the journal is what it concluded
 * from — every event, which door, and which direction the terminal recorded it as. That
 * last one is what usually explains an episode nobody recognises: a scan the reader
 * remembers as leaving, recorded by a terminal set to `entry`, closes no episode and
 * opens another. Above this line that is invisible; below it, it is the row.
 *
 * Collapsed by default: a month is thirty rows, and thirty expanded rows is a wall —
 * and the journal is fetched only for a day someone actually opened, which is the other
 * half of why it is collapsed (see DayLog: no route filters the log by date).
 */
const styles = StyleSheet.create({
  root: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  restful: {opacity: 0.55},
  date: {
    width: 46,
    paddingVertical: spacing(1),
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNum: {fontFamily: fonts.mono.medium, fontSize: 18, lineHeight: 22},
  body: {flex: 1, gap: 2},
  flags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing(1),
    justifyContent: 'flex-end',
    flexShrink: 1,
    maxWidth: '42%',
  },
  details: {
    gap: spacing(2),
    marginTop: spacing(3),
    paddingTop: spacing(3),
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  chevron: {transform: [{rotate: '90deg'}]},
  chevronOpen: {transform: [{rotate: '270deg'}]},
  journal: {
    gap: spacing(2),
    marginTop: spacing(1),
    paddingTop: spacing(3),
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  interval: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  intervalText: {flex: 1},
  durations: {flexDirection: 'row', gap: spacing(3), flexWrap: 'wrap'},
  duration: {gap: 1, minWidth: 120},
});

/**
 * One day, collapsed to its arrival→departure pair and expandable into the episodes
 * behind it. Exported because the month calendar shows exactly this card for the day a
 * reader picked out of the grid — the same card in both places, so a day never looks
 * like two different things.
 */
export function PersonDay({
  day,
  defaultOpen = false,
  companyId,
  recordId,
  onOpenArchive,
}: {
  day: AttendanceDayRecord;
  defaultOpen?: boolean;
  /**
   * Whose day this is. Both absent — the card stays what it always was, a day without
   * its journal; a screen that has no company in hand simply does not ask for one.
   */
  companyId?: string;
  /** Absent WITH a `companyId` ⇒ the reader's own day, read through `/me/logs`. */
  recordId?: string;
  /** Open the company log archive on this day. Employer-side screens only. */
  onOpenArchive?: (date: string) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  const flags = knownFlags(day.flags);
  const restful = !day.scheduled && day.event_count === 0;
  const intervals = day.intervals ?? [];
  const expandable = intervals.length > 0 || day.event_count > 0;

  const hour = t('attendance.units.hour');
  const minute = t('attendance.units.minute');

  const arrival = formatClock(day.arrival_time_millis);
  const departure = formatClock(day.departure_time_millis);
  const hasPair =
    day.arrival_time_millis != null || day.departure_time_millis != null;

  return (
    <Card
      padding={3}
      variant={restful ? 'flat' : 'default'}
      style={restful ? styles.restful : undefined}>
      <Pressable
        accessibilityRole={expandable ? 'button' : undefined}
        accessibilityState={expandable ? {expanded: open} : undefined}
        onPress={expandable ? () => setOpen(v => !v) : undefined}
        testID={`report-day-${day.date}`}>
        <View style={styles.row}>
          <View style={[styles.date, {backgroundColor: p.muted}]}>
            <AppText style={styles.dayNum}>{formatDayOfMonth(day.date)}</AppText>
            <AppText variant="micro">{t(`attendance.weekday.${day.weekday}`)}</AppText>
          </View>

          <View style={styles.body}>
            <AppText
              variant="monoLg"
              color={hasPair ? p.foreground : p.mutedForeground}>
              {hasPair ? `${arrival} → ${departure}` : '—'}
            </AppText>
            {day.worked_minutes != null ? (
              <AppText variant="caption">
                {t('attendance.worked', {
                  value: formatHoursFromMinutes(day.worked_minutes, hour, minute),
                })}
              </AppText>
            ) : null}
          </View>

          {flags.length > 0 ? (
            <View style={styles.flags}>
              {flags.map(f => (
                <StatusPill
                  key={f}
                  tone={flagTone(f)}
                  size="sm"
                  dot={false}
                  label={t(flagLabelKey(f))}
                />
              ))}
            </View>
          ) : null}

          {/* A row that opens has to look like one. Without the chevron the whole
              intervals view — the answer to "was that nine hours in the building or
              three visits" — is behind an affordance nobody knows is there. */}
          {expandable ? (
            <View style={open ? styles.chevronOpen : styles.chevron}>
              <Icon name="chevron" size={16} color={p.mutedForeground} />
            </View>
          ) : null}
        </View>
      </Pressable>

      {open ? (
        <View style={[styles.details, {borderTopColor: p.border}]}>
          <View style={styles.durations}>
            <View style={styles.duration}>
              <AppText variant="mono">
                {day.worked_minutes != null
                  ? formatHoursFromMinutes(day.worked_minutes, hour, minute)
                  : '—'}
              </AppText>
              <AppText variant="micro">{t('reports.spanWithBreaks')}</AppText>
            </View>
            <View style={styles.duration}>
              <AppText variant="mono">
                {day.interval_minutes != null
                  ? formatHoursFromMinutes(day.interval_minutes, hour, minute)
                  : '—'}
              </AppText>
              <AppText variant="micro">{t('reports.spanInside')}</AppText>
            </View>
            <View style={styles.duration}>
              <AppText variant="mono">{day.event_count}</AppText>
              <AppText variant="micro">{t('reports.csv.events')}</AppText>
            </View>
          </View>

          {intervals.length > 0 ? (
            intervals.map((interval, i) => (
              <View key={`${interval.start_millis}-${i}`} style={styles.interval}>
                <AppText variant="mono" color={p.primary}>
                  {formatClock(interval.start_millis)} →{' '}
                  {interval.end_millis == null
                    ? '…'
                    : formatClock(interval.end_millis)}
                </AppText>
                <AppText variant="micro" style={styles.intervalText} numberOfLines={1}>
                  {[interval.start_device_name, interval.end_device_name]
                    .filter(Boolean)
                    .join(' · ')}
                </AppText>
                <AppText variant="micro">
                  {interval.minutes == null
                    ? t('reports.intervalOpen')
                    : formatHoursFromMinutes(interval.minutes, hour, minute)}
                </AppText>
              </View>
            ))
          ) : (
            <AppText variant="caption">{t('reports.noIntervals')}</AppText>
          )}

          {companyId ? (
            <View style={[styles.journal, {borderTopColor: p.border}]}>
              <DayLog
                companyId={companyId}
                recordId={recordId}
                date={day.date}
                expectedCount={day.event_count}
                onOpenArchive={
                  onOpenArchive ? () => onOpenArchive(day.date) : undefined
                }
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </Card>
  );
}

/**
 * Дни человека за период — списком, и с одним фильтром.
 *
 * Фильтр здесь ровно один и назван вопросом, который к этому списку и задают: «что не
 * так». Месяц — это тридцать строк, из которых двадцать восемь скучные; человек
 * открывает список, чтобы найти те две. Сортировки нет намеренно: день читают ПО
 * ПОРЯДКУ, и переставить его — значит сломать единственное, чем этот список полезен.
 */
export function PersonDayList({
  days,
  companyId,
  recordId,
  onOpenArchive,
}: {
  days: AttendanceDayRecord[];
  companyId?: string;
  recordId?: string;
  onOpenArchive?: (date: string) => void;
}) {
  const {t} = useTranslation();
  const [only, setOnly] = useState('');

  const shown = days.filter(day => {
    if (!only) {
      return true;
    }
    const flags = knownFlags(day.flags);
    // «Всё в порядке» — это НЕ отсутствие отметок: день без графика (`unscheduled`) и
    // день, о котором нечего сказать, — разные вещи, и ни один из них не проблема.
    const trouble = flags.some(f =>
      ['absent', 'late', 'earlyLeave', 'incomplete'].includes(f),
    );
    return only === 'trouble' ? trouble : !trouble;
  });

  return (
    <View style={styles.root}>
      {days.length > 7 ? (
        <FilterBar testID="person-days-filters">
          <FilterSelect
            label={t('reports.dayFilter')}
            value={only}
            options={[
              {value: '', label: t('actions.any')},
              {value: 'trouble', label: t('reports.dayTrouble')},
              {value: 'clean', label: t('reports.dayClean')},
            ]}
            onChange={setOnly}
            phone
            testID="person-days-only"
          />
        </FilterBar>
      ) : null}

      {shown.length === 0 ? (
        <HelpNote text={t('reports.dayNoneMatch')} />
      ) : (
        shown.map(day => (
          <PersonDay
            key={day.date}
            day={day}
            companyId={companyId}
            recordId={recordId}
            onOpenArchive={onOpenArchive}
          />
        ))
      )}
    </View>
  );
}
